import React, { useMemo } from 'react';
import PageSkeleton from './PageSkeleton';

function DataTable({
    columns,
    data,
    sortConfig,
    onSort,
    loading,
    emptyMessage = "No records found",
    page,
    pageSize,
    totalEntries,
    totalPages,
    onPageChange,
    onPageSizeChange,
    onRowClick,
    onRowDoubleClick,
    selectedRowIndex,
    footerRow,
    searchBar
}) {
    const showingFrom = totalEntries === 0 ? 0 : (page - 1) * pageSize + 1;
    const showingTo = Math.min(totalEntries, (page - 1) * pageSize + data.length);

    const pages = useMemo(() => {
        const out = [];
        const maxButtons = 5;
        let start = Math.max(1, page - Math.floor(maxButtons / 2));
        let end = Math.min(totalPages, start + maxButtons - 1);
        start = Math.max(1, end - maxButtons + 1);
        for (let i = start; i <= end; i += 1) out.push(i);
        return out;
    }, [page, totalPages]);

    if (loading) {
        return (
            <>
                {searchBar && (
                    <div className="list-page-toolbar">
                        {searchBar}
                    </div>
                )}
                <PageSkeleton />
            </>
        );
    }

    return (
        <>
            {searchBar && (
                <div className="list-page-toolbar">
                    {searchBar}
                </div>
            )}
            <div className="list-page-table-wrapper">
                <table className="list-page-table">
                    <thead>
                        <tr>
                            {columns.map((col, idx) => {
                                if (col.hidden) return null;
                                return (
                                    <th 
                                        key={col.key || idx} 
                                        onClick={() => col.sortable && onSort && onSort(col.key)}
                                        style={{ cursor: col.sortable ? 'pointer' : 'default', ...(col.style || {}) }}
                                    >
                                        {col.label} 
                                        {col.sortable && sortConfig?.key === col.key ? (sortConfig.direction === 'ascending' ? ' ▲' : ' ▼') : ''}
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {data.length === 0 ? (
                            <tr>
                                <td className="list-page-table-empty" colSpan={columns.filter(c => !c.hidden).length}>
                                    {emptyMessage}
                                </td>
                            </tr>
                        ) : (
                            data.map((row, idx) => (
                                <tr 
                                    key={row._id || row.id || idx} 
                                    onDoubleClick={(e) => onRowDoubleClick && onRowDoubleClick(row, idx, e)}
                                    onClick={(e) => onRowClick && onRowClick(row, idx, e)}
                                    style={{ 
                                        cursor: onRowDoubleClick || onRowClick ? 'pointer' : 'default',
                                        backgroundColor: idx === selectedRowIndex ? '#fcfdfd' : 'transparent'
                                    }}
                                >
                                    {columns.map((col, colIdx) => {
                                        if (col.hidden) return null;
                                        return (
                                            <td key={colIdx} style={col.cellStyle ? col.cellStyle(row) : {}}>
                                                {col.render ? col.render(row, idx) : row[col.key]}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))
                        )}
                        {footerRow}
                    </tbody>
                </table>
            </div>

            <div className="list-page-footer">
                <div className="list-page-entries">
                    <span>Show</span>
                    <select
                        className="list-page-entries-select"
                        value={pageSize}
                        onChange={(e) => {
                            onPageSizeChange(Number(e.target.value));
                            onPageChange(1);
                        }}
                    >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                    </select>
                    <span>entries</span>
                </div>

                <div className="list-page-info">
                    Showing {showingFrom} to {showingTo} of {totalEntries} entries
                </div>

                <div className="list-page-pagination">
                    <button
                        type="button"
                        className="list-page-page-btn"
                        disabled={page <= 1}
                        onClick={() => onPageChange((p) => Math.max(1, p - 1))}
                    >
                        Previous
                    </button>

                    {pages.map((p) => (
                        <button
                            key={p}
                            type="button"
                            className={`list-page-page-num ${p === page ? 'list-page-page-num-active' : ''}`}
                            onClick={() => onPageChange(p)}
                        >
                            {p}
                        </button>
                    ))}

                    <button
                        type="button"
                        className="list-page-page-btn"
                        disabled={page >= totalPages}
                        onClick={() => onPageChange((p) => Math.min(totalPages, p + 1))}
                    >
                        Next
                    </button>
                </div>
            </div>
        </>
    );
}

export default DataTable;
